import { useEditFacility } from "../../../api/facility";
import TailwindBottomButtonView from "../../../components/mainBottomButtonView/TailwindBottomButtonView";
import FacilityDrawerFormBody from "./components/FacilityDrawerFormBody";
import FacilityDrawerShell from "./components/FacilityDrawerShell";
import RecordDeleteConfirm from "./components/RecordDeleteConfirm";
import { checkFacilitySaveCondition } from "./helpers/facilityValidation.helpers";
import { useFacilityCountryOptions } from "./hooks/useFacilityCountryOptions";
import { useFacilityDrawerForm } from "./hooks/useFacilityDrawerForm";
import { useFacilitySourceTypeOptions } from "./hooks/useFacilitySourceTypeOptions";
import {
  FacilityDraftFormValues,
  FacilityEditModalProps,
  SourceCollectionKey
} from "./types/facilityDrawer.types";
import { buildEditFacilityPayload } from "./utils/facilityDraft.utils";

const FacilityEditDrawer = ({
  visibleModal,
  setVisibleModal,
  facilityData,
  updateFacilityData
}: FacilityEditModalProps) => {
  const { energyOptions, heatOptions, waterOptions, refrigerantOptions, isOptionsLoading } =
    useFacilitySourceTypeOptions();
  const { countryOptions, isCountriesLoading } = useFacilityCountryOptions();

  const {
    form,
    energyArray,
    heatArray,
    waterArray,
    refrigerantArray,
    distributionErrors,
    saveDisabled,
    pendingDelete,
    requestDeleteRecord,
    cancelDeleteRecord,
    confirmDeleteRecord,
    addSourceRecord,
    addEnergyRecord,
    submitForm
  } = useFacilityDrawerForm({ visibleModal, mode: "edit", initialFacility: facilityData });
  const { isLoading: isSaving, mutate } = useEditFacility();

  const closeModal = () => setVisibleModal(false);

  // Legacy calls setVisibleModal(true) both inside onSuccess and unconditionally right after
  // mutate() (FacilityEditModalLegacy.onSubmit) — every host ignores the argument and just
  // closes + refetches, so this fires the list refetch twice today. Replicated deliberately;
  // de-duplicating it is a separate, non-UI ticket (see plan risk R7).
  const onSubmit = (values: FacilityDraftFormValues) => {
    if (checkFacilitySaveCondition("edit", values)) {
      return;
    }

    const payload = buildEditFacilityPayload(values, facilityData?._id);
    mutate(
      { data: payload, simulatedFacility: false },
      {
        onSuccess: () => {
          updateFacilityData?.(() => payload);
          setVisibleModal(true);
        }
      }
    );
    setVisibleModal(true);
  };

  const sourceFields = {
    heatSource: heatArray.fields,
    waterSource: waterArray.fields,
    refrigerantSource: refrigerantArray.fields
  };

  const sourceOptionsFor = (key: SourceCollectionKey) => {
    if (key === "heatSource") return heatOptions ?? [];
    if (key === "waterSource") return waterOptions ?? [];
    return refrigerantOptions ?? [];
  };

  const removeSourceRecord = (key: SourceCollectionKey, index: number) =>
    requestDeleteRecord(key, index);

  return (
    <FacilityDrawerShell
      open={visibleModal}
      onOpenChange={open => !open && closeModal()}
      eyebrow="Facility"
      title="Edit facility"
      footer={
        <TailwindBottomButtonView
          cancelButtonStatus
          cancelButton={closeModal}
          cancelButtonTitle="Cancel"
          saveButtonStatus
          saveButton={submitForm(onSubmit)}
          saveTitle="Save changes"
          saveDisable={saveDisabled}
          saveLoading={isSaving}
          customHeight={72}
          customBottomContentMain="!tw-px-2 tw-py-3"
          testId="facility-edit-bottom-button"
        />
      }
    >
      <FacilityDrawerFormBody
        form={form}
        isOptionsLoading={isOptionsLoading}
        countryOptions={countryOptions}
        isCountriesLoading={isCountriesLoading}
        energyOptions={energyOptions ?? []}
        sourceOptionsFor={sourceOptionsFor}
        energyFields={energyArray.fields}
        sourceFields={sourceFields}
        distributionErrors={distributionErrors}
        onAddEnergyRecord={addEnergyRecord}
        onAddSourceRecord={addSourceRecord}
        onRemoveEnergyRecord={index => requestDeleteRecord("energy", index)}
        onRemoveSourceRecord={removeSourceRecord}
      />

      <RecordDeleteConfirm
        isOpen={pendingDelete !== null}
        onClose={cancelDeleteRecord}
        onConfirm={confirmDeleteRecord}
      />
    </FacilityDrawerShell>
  );
};

export default FacilityEditDrawer;
